import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wallet, CurrencyDollar, Lightning, ArrowUp, ArrowDown, ShoppingCart,
  CalendarBlank, CreditCard, ChartLineUp, Star, CheckCircle, Clock, Coins, Receipt, ChartBar,
  TrendUp, TrendDown, Timer, ShareNetwork, Copy, Gift
} from '@phosphor-icons/react';
import { getWallet, getWalletLedger, getCreditPacks, getSubscriptionPlans, createSubscription, packCheckout, confirmPackPurchase, getActionCosts, getWalletAnalytics, getWalletPredictions, getReferralInfo, applyReferralCode, getUniversalKeyStatus } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { PageHero } from '../components/motion/PageHero';

const TYPE_LABELS = {
  monthly_allocation: 'Dotation mensuelle',
  pack_purchase: 'Achat pack',
  consumption: 'Consommation',
  credit: 'Credit',
  debit: 'Debit',
  referral_bonus: 'Bonus parrainage',
};

const TYPE_COLORS = {
  monthly_allocation: 'bg-blue-100 text-blue-800',
  pack_purchase: 'bg-green-100 text-green-800',
  consumption: 'bg-orange-100 text-orange-800',
  credit: 'bg-emerald-100 text-emerald-800',
  debit: 'bg-red-100 text-red-800',
};

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [universalKey, setUniversalKey] = useState(null);
  const [ledger, setLedger] = useState({ entries: [], total: 0 });
  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [actionCosts, setActionCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [costsDialogOpen, setCostsDialogOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [ledgerFilter, setLedgerFilter] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState('30');
  const [predictions, setPredictions] = useState(null);
  const [referral, setReferral] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchData = async () => {
    try {
      const [walletRes, ledgerRes, packsRes, plansRes, costsRes, analyticsRes, predictionsRes, referralRes] = await Promise.all([
        getWallet(), getWalletLedger(0, 30), getCreditPacks(), getSubscriptionPlans(), getActionCosts(), getWalletAnalytics(30), getWalletPredictions(), getReferralInfo(),
      ]);
      setWallet(walletRes.data);
      setLedger(ledgerRes.data);
      setPacks(packsRes.data.packs);
      setPlans(plansRes.data.plans);
      setActionCosts(costsRes.data.costs);
      setAnalytics(analyticsRes.data);
      setPredictions(predictionsRes.data);
      setReferral(referralRes.data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    getUniversalKeyStatus().then(r => setUniversalKey(r.data)).catch(() => {});
  }, []);

  // Handle checkout return from Stripe
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const pack = searchParams.get('pack');
    const sessionId = searchParams.get('session_id');
    if (checkout === 'success' && pack && sessionId) {
      confirmPackPurchase(pack, sessionId).then(() => {
        toast.success('Pack achete avec succes !');
        fetchData();
      }).catch(() => toast.error('Erreur lors de la confirmation'));
    } else if (checkout === 'cancel') {
      toast.info('Achat annule');
    }
  }, [searchParams]);

  // Filtered ledger
  useEffect(() => {
    const fetchLedger = async () => {
      const type = ledgerFilter === 'all' ? null : ledgerFilter;
      const res = await getWalletLedger(0, 30, null, type);
      setLedger(res.data);
    };
    if (!loading) fetchLedger();
  }, [ledgerFilter]);

  // Analytics period change
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await getWalletAnalytics(parseInt(analyticsDays));
        setAnalytics(res.data);
      } catch { /* ignore */ }
    };
    if (!loading) fetchAnalytics();
  }, [analyticsDays]);

  const handleBuyPack = async (packId) => {
    setPurchasing(packId);
    try {
      const res = await packCheckout(packId);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erreur';
      if (msg.includes('Stripe')) {
        // Fallback: confirm directly for dev/test
        try {
          await confirmPackPurchase(packId, 'dev_test');
          toast.success('Pack achete (mode test)');
          fetchData();
          setPackDialogOpen(false);
        } catch {
          toast.error('Erreur lors de l\'achat');
        }
      } else {
        toast.error(msg);
      }
    } finally {
      setPurchasing(null);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      await createSubscription(planId, 'monthly');
      toast.success('Abonnement active !');
      fetchData();
      setPlanDialogOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur');
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) return;
    setApplyingCode(true);
    try {
      const res = await applyReferralCode(referralCode.trim());
      toast.success(res.data.message);
      setReferralCode('');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Code invalide');
    } finally { setApplyingCode(false); }
  };

  const copyReferralCode = () => {
    if (referral?.code) {
      navigator.clipboard.writeText(referral.code);
      toast.success('Code copie !');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  const sub = wallet?.subscription;
  const balancePercent = wallet?.total_credited > 0
    ? Math.round(((wallet.total_credited - wallet.total_consumed) / wallet.total_credited) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="wallet-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Wallet & Crédits"
        subtitle="Solde universel key, achats et historique de consommation"
        icon={<Wallet size={24} weight="duotone" className="text-white" />}
        gradient="from-[#80C51F] to-[#2D5A4C]"
        accents={['#FF8E00', '#00AEEF']}
      />

      {/* Universal Key Status */}
      <Card className="border-[#E8E6E1]" data-testid="universal-key-card">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${universalKey?.configured ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                <Coins size={20} className={universalKey?.configured ? 'text-emerald-700' : 'text-orange-600'} />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Universal Key {universalKey?.configured ? '· Configurée' : '· Non configurée'}</p>
                <p className="text-xs text-[#5E6D66]">
                  {universalKey?.configured ? (
                    <>Clé active : <span className="font-mono" data-testid="universal-key-masked">{universalKey.masked}</span></>
                  ) : (
                    <>Ajoutez du solde via <em>Profile → Manage plan → Universal Key → Add Balance</em></>
                  )}
                </p>
                {universalKey?.supports && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {universalKey.supports.map(s => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1EFE9] text-[#5E6D66]">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <a
              href="/profile"
              className="text-xs text-[#2D5A4C] hover:underline font-medium"
              data-testid="universal-key-manage"
            >Gérer →</a>
          </div>
        </CardContent>
      </Card>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-[#2D5A4C]/20 bg-gradient-to-br from-[#2D5A4C] to-[#1E4136] text-white" data-testid="balance-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-white/70">Solde disponible</span>
              <Coins size={24} className="text-white/60" />
            </div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>{(wallet?.total_available || 0).toLocaleString('fr-FR')} UC</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-white/70">
              <span>Principal: {(wallet?.balance || 0).toLocaleString('fr-FR')}</span>
              <span>Bonus: {(wallet?.bonus_balance || 0).toLocaleString('fr-FR')}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E6E1]" data-testid="credited-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[#5E6D66]">Total credite</span>
              <ArrowDown size={20} className="text-green-500" />
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{(wallet?.total_credited || 0).toLocaleString('fr-FR')} UC</p>
            <div className="w-full h-1.5 bg-[#E8E6E1] rounded-full mt-3">
              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${balancePercent}%` }} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E6E1]" data-testid="consumed-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[#5E6D66]">Total consomme</span>
              <ArrowUp size={20} className="text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{(wallet?.total_consumed || 0).toLocaleString('fr-FR')} UC</p>
            <p className="text-xs text-[#5E6D66] mt-2">{ledger.total} transaction(s)</p>
          </CardContent>
        </Card>

        <Card className="border-[#E8E6E1]" data-testid="subscription-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[#5E6D66]">Abonnement</span>
              <CreditCard size={20} className="text-[#2D5A4C]" />
            </div>
            {sub ? (
              <>
                <p className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{sub.plan_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-green-100 text-green-800 text-xs">Actif</Badge>
                  <span className="text-xs text-[#5E6D66]">{sub.monthly_allocation} UC/mois</span>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[#5E6D66]">Aucun abonnement</p>
                <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => setPlanDialogOpen(true)} data-testid="subscribe-btn">
                  Souscrire
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Predictions & Referral */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Predictions */}
        {predictions && (
          <Card className="border-[#E8E6E1]" data-testid="predictions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Timer size={18} className="text-[#2D5A4C]" />
                Previsions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F9F8F5] rounded-lg p-3">
                  <p className="text-[10px] text-[#5E6D66] uppercase font-medium">Jours restants</p>
                  <p className="text-2xl font-bold text-[#1A2421] mt-1" style={{ fontFamily: 'Manrope' }}>
                    {predictions.days_remaining < 0 ? '∞' : predictions.days_remaining}
                  </p>
                  <p className="text-[10px] text-[#5E6D66]">au rythme actuel</p>
                </div>
                <div className="bg-[#F9F8F5] rounded-lg p-3">
                  <p className="text-[10px] text-[#5E6D66] uppercase font-medium">Moy. journaliere</p>
                  <p className="text-2xl font-bold text-[#1A2421] mt-1" style={{ fontFamily: 'Manrope' }}>
                    {predictions.avg_daily_consumption}
                  </p>
                  <p className="text-[10px] text-[#5E6D66]">UC/jour</p>
                </div>
              </div>
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-[#F9F8F5]">
                {predictions.trend === 'hausse' ? (
                  <TrendUp size={18} className="text-red-500" />
                ) : predictions.trend === 'baisse' ? (
                  <TrendDown size={18} className="text-green-500" />
                ) : (
                  <ChartLineUp size={18} className="text-[#5E6D66]" />
                )}
                <span className="text-sm text-[#1A2421]">
                  Tendance: <span className={`font-medium ${predictions.trend === 'hausse' ? 'text-red-500' : predictions.trend === 'baisse' ? 'text-green-500' : 'text-[#5E6D66]'}`}>
                    {predictions.trend === 'hausse' ? `En hausse (+${predictions.trend_percent}%)` : predictions.trend === 'baisse' ? `En baisse (${predictions.trend_percent}%)` : 'Stable'}
                  </span>
                </span>
              </div>
              {predictions.recommended_pack && (
                <div className="border border-[#2D5A4C]/20 rounded-lg p-3 bg-[#2D5A4C]/5">
                  <p className="text-[10px] text-[#2D5A4C] font-semibold uppercase mb-1">Pack recommande</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-[#1A2421]">{predictions.recommended_pack.name}</p>
                      <p className="text-xs text-[#5E6D66]">{predictions.recommended_pack.credits}+{predictions.recommended_pack.bonus} UC = {predictions.recommended_pack.price} EUR</p>
                    </div>
                    <Button size="sm" onClick={() => setPackDialogOpen(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136] text-xs" data-testid="prediction-buy-btn">
                      Acheter
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Referral */}
        {referral && (
          <Card className="border-[#E8E6E1]" data-testid="referral-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <ShareNetwork size={18} className="text-[#2D5A4C]" />
                Parrainage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#F9F8F5] rounded-lg p-4">
                <p className="text-xs text-[#5E6D66] mb-2">Votre code de parrainage</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xl font-mono font-bold text-[#2D5A4C] tracking-widest" data-testid="referral-code">
                    {referral.code}
                  </code>
                  <Button variant="outline" size="sm" onClick={copyReferralCode} data-testid="copy-code-btn">
                    <Copy size={14} className="mr-1" />
                    Copier
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#1A2421]">{referral.total_referrals}</p>
                    <p className="text-[10px] text-[#5E6D66]">Filleul(s)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{referral.total_bonus_earned} UC</p>
                    <p className="text-[10px] text-[#5E6D66]">Bonus gagnes</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#5E6D66]">
                <Gift size={14} className="text-[#2D5A4C]" />
                Parrain: +{referral.bonus_per_referral} UC bonus | Filleul: +{referral.bonus_for_referee} UC bonus
              </div>
              <div>
                <p className="text-xs text-[#5E6D66] mb-1.5">Appliquer un code parrainage</p>
                <div className="flex gap-2">
                  <Input
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="CODE"
                    className="h-8 text-xs font-mono uppercase"
                    data-testid="referral-input"
                  />
                  <Button size="sm" onClick={handleApplyReferral} disabled={applyingCode || !referralCode.trim()} className="bg-[#2D5A4C] hover:bg-[#1E4136] text-xs" data-testid="apply-code-btn">
                    {applyingCode ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Appliquer'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Usage Analytics */}
      {analytics && (
        <div className="space-y-4" data-testid="analytics-section">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
              <ChartBar size={20} className="inline mr-2 text-[#2D5A4C]" />
              Utilisation des credits
            </h2>
            <Select value={analyticsDays} onValueChange={setAnalyticsDays}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="analytics-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 derniers jours</SelectItem>
                <SelectItem value="30">30 derniers jours</SelectItem>
                <SelectItem value="90">3 derniers mois</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Flow Summary */}
            <Card className="border-[#E8E6E1]" data-testid="flow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#5E6D66]">Flux credits / debits</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-around py-4">
                  <div className="text-center">
                    <ArrowDown size={24} className="mx-auto text-green-500 mb-1" />
                    <p className="text-2xl font-bold text-green-600">{(analytics.flow?.credits || 0).toLocaleString('fr-FR')}</p>
                    <p className="text-[10px] text-[#5E6D66] uppercase">Credits</p>
                  </div>
                  <div className="w-px h-12 bg-[#E8E6E1]" />
                  <div className="text-center">
                    <ArrowUp size={24} className="mx-auto text-orange-500 mb-1" />
                    <p className="text-2xl font-bold text-orange-600">{(analytics.flow?.debits || 0).toLocaleString('fr-FR')}</p>
                    <p className="text-[10px] text-[#5E6D66] uppercase">Debits</p>
                  </div>
                  <div className="w-px h-12 bg-[#E8E6E1]" />
                  <div className="text-center">
                    <Coins size={24} className="mx-auto text-[#2D5A4C] mb-1" />
                    <p className="text-2xl font-bold text-[#2D5A4C]">{((analytics.flow?.credits || 0) - (analytics.flow?.debits || 0)).toLocaleString('fr-FR')}</p>
                    <p className="text-[10px] text-[#5E6D66] uppercase">Net</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Consumption by action (bar chart) */}
            <Card className="border-[#E8E6E1]" data-testid="by-action-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#5E6D66]">Consommation par action</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.by_action?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={analytics.by_action.slice(0, 8)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                      <XAxis dataKey="action" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => [`${v} UC`, 'Total']} labelStyle={{ fontSize: 11 }} />
                      <Bar dataKey="total" fill="#2D5A4C" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center py-8 text-sm text-[#5E6D66]">Aucune consommation sur la periode</p>
                )}
              </CardContent>
            </Card>

            {/* Daily consumption (area chart) */}
            <Card className="border-[#E8E6E1]" data-testid="daily-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#5E6D66]">Consommation journaliere</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.daily?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={analytics.daily} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => [`${v} UC`, 'Consomme']} labelFormatter={(l) => `Date: ${l}`} />
                      <Area type="monotone" dataKey="total" stroke="#D47A62" fill="#D47A62" fillOpacity={0.15} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center py-8 text-sm text-[#5E6D66]">Aucune donnee sur la periode</p>
                )}
              </CardContent>
            </Card>

            {/* Credit sources (pie chart) */}
            <Card className="border-[#E8E6E1]" data-testid="sources-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#5E6D66]">Sources de credits</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.credit_sources?.length > 0 ? (
                  <div className="flex items-center gap-6">
                    <ResponsiveContainer width="50%" height={160}>
                      <PieChart>
                        <Pie
                          data={analytics.credit_sources}
                          dataKey="total"
                          nameKey="type"
                          cx="50%"
                          cy="50%"
                          outerRadius={60}
                          innerRadius={30}
                        >
                          {analytics.credit_sources.map((_, i) => (
                            <Cell key={i} fill={['#2D5A4C', '#D47A62', '#8DB0A1', '#5E6D66'][i % 4]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => [`${v} UC`, '']} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {analytics.credit_sources.map((src, i) => (
                        <div key={src.type} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: ['#2D5A4C', '#D47A62', '#8DB0A1', '#5E6D66'][i % 4] }} />
                          <span className="text-xs text-[#5E6D66]">{TYPE_LABELS[src.type] || src.type}: <span className="font-medium text-[#1A2421]">{src.total} UC</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center py-8 text-sm text-[#5E6D66]">Aucun credit sur la periode</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Ledger History */}
      <Card className="border-[#E8E6E1]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              <Receipt size={20} className="inline mr-2 text-[#2D5A4C]" />
              Historique des transactions
            </CardTitle>
            <Select value={ledgerFilter} onValueChange={setLedgerFilter}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="ledger-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="monthly_allocation">Dotations mensuelles</SelectItem>
                <SelectItem value="pack_purchase">Achats packs</SelectItem>
                <SelectItem value="consumption">Consommations</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ledger.entries.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={32} className="mx-auto text-[#E8E6E1] mb-2" />
              <p className="text-sm text-[#5E6D66]">Aucune transaction</p>
            </div>
          ) : (
            <div className="divide-y divide-[#F0EFEB]">
              {ledger.entries.map((entry, idx) => (
                <div key={entry.id || idx} className="flex items-center gap-4 px-6 py-3 hover:bg-[#FAFAF8] transition-colors" data-testid={`ledger-entry-${idx}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    entry.direction === 'credit' ? 'bg-green-100' : 'bg-orange-100'
                  }`}>
                    {entry.direction === 'credit' ? (
                      <ArrowDown size={14} className="text-green-600" />
                    ) : (
                      <ArrowUp size={14} className="text-orange-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A2421] truncate">{entry.reason}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className={`text-[10px] ${TYPE_COLORS[entry.type] || 'bg-gray-100 text-gray-800'}`}>
                        {TYPE_LABELS[entry.type] || entry.type}
                      </Badge>
                      <span className="text-[10px] text-[#8DB0A1]">
                        {new Date(entry.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${entry.direction === 'credit' ? 'text-green-600' : 'text-orange-600'}`}>
                      {entry.direction === 'credit' ? '+' : '-'}{entry.amount} UC
                    </p>
                    {entry.bonus > 0 && (
                      <p className="text-[10px] text-green-500">+{entry.bonus} bonus</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pack Purchase Dialog */}
      <Dialog open={packDialogOpen} onOpenChange={setPackDialogOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="pack-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <ShoppingCart size={20} className="inline mr-2 text-[#2D5A4C]" />
              Acheter des credits
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Selectionnez un pack de credits. Les bonus sont offerts !
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {packs.map((pack) => (
              <div
                key={pack.id}
                className={`relative border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                  pack.popular ? 'border-[#2D5A4C] ring-2 ring-[#2D5A4C]/20' : 'border-[#E8E6E1]'
                }`}
                data-testid={`pack-${pack.id}`}
              >
                {pack.popular && (
                  <Badge className="absolute -top-2 right-3 bg-[#2D5A4C] text-white text-[10px]">
                    <Star size={10} className="mr-0.5" weight="fill" />
                    Populaire
                  </Badge>
                )}
                <p className="text-sm font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{pack.name}</p>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-[#2D5A4C]">{pack.credits.toLocaleString('fr-FR')}</span>
                  <span className="text-xs text-[#5E6D66] ml-1">UC</span>
                </div>
                {pack.bonus > 0 && (
                  <p className="text-xs text-green-600 font-medium mt-1">
                    + {pack.bonus} bonus offerts !
                  </p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-lg font-bold text-[#1A2421]">{pack.price} EUR</span>
                  <Button
                    size="sm"
                    onClick={() => handleBuyPack(pack.id)}
                    disabled={purchasing === pack.id}
                    className="bg-[#2D5A4C] hover:bg-[#1E4136] text-xs"
                    data-testid={`buy-${pack.id}`}
                  >
                    {purchasing === pack.id ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <CreditCard size={14} className="mr-1" />
                    )}
                    Acheter
                  </Button>
                </div>
                <p className="text-[10px] text-[#8DB0A1] mt-1">{pack.price_per_uc} EUR/UC</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Plans Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <ChartLineUp size={20} className="inline mr-2 text-[#2D5A4C]" />
              Choisir un abonnement
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Chaque abonnement inclut un acces complet + une dotation mensuelle en credits.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            {plans.map((plan) => {
              const isActive = sub?.plan_id === plan.id;
              return (
                <div
                  key={plan.id}
                  className={`border rounded-xl p-5 ${
                    plan.id === 'pro' ? 'border-[#2D5A4C] ring-2 ring-[#2D5A4C]/20' : 'border-[#E8E6E1]'
                  } ${isActive ? 'bg-[#F9F8F5]' : ''}`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.id === 'pro' && (
                    <Badge className="bg-[#2D5A4C] text-white text-[10px] mb-2">Recommande</Badge>
                  )}
                  <h3 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-[#2D5A4C]">{plan.price_monthly}</span>
                    <span className="text-sm text-[#5E6D66]"> EUR/mois</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-sm text-[#2D5A4C] font-medium">
                    <Lightning size={14} weight="fill" />
                    {plan.monthly_allocation} UC/mois inclus
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-[#5E6D66]">
                        <CheckCircle size={12} className="text-green-500 flex-shrink-0" weight="fill" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-4 text-xs ${isActive ? 'bg-gray-400' : 'bg-[#2D5A4C] hover:bg-[#1E4136]'}`}
                    disabled={isActive}
                    onClick={() => handleSubscribe(plan.id)}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {isActive ? 'Actif' : 'Souscrire'}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Costs Dialog */}
      <Dialog open={costsDialogOpen} onOpenChange={setCostsDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="costs-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <Lightning size={20} className="inline mr-2 text-[#2D5A4C]" />
              Grille tarifaire des actions
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Chaque action CRM consomme des Unites de Credit (UC).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {actionCosts.map((cost) => (
              <div key={cost.action} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-[#F9F8F5] transition-colors" data-testid={`cost-${cost.action}`}>
                <div>
                  <p className="text-sm font-medium text-[#1A2421]">{cost.label}</p>
                  <p className="text-[10px] text-[#8DB0A1] uppercase">{cost.category}</p>
                </div>
                <Badge className="bg-[#2D5A4C]/10 text-[#2D5A4C] font-bold">{cost.cost} UC</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
